import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote } from "lucide-react";

const testimonials = [
  {
    quote: "I was skeptical at first, honestly. But after 10 weeks on the program, I dropped 18 pounds and my energy completely turned around. I'm hiking again, sleeping through the night, and I actually look forward to mornings now. My husband started asking what changed.",
    name: "Lisa Brennan",
    role: "Yoga Instructor & Mother of 3, Age 46",
    image: "https://images.unsplash.com/photo-1595433502559-d8f05e6a1041?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtaWRkbGUlMjBhZ2VkJTIwd29tYW4lMjBoZWFsdGh5JTIwbGlmZXN0eWxlJTIwcG9ydHJhaXQlMjBvdXRkb29yfGVufDB8fHx8MTc3NjE4MzY4M3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "Tore my ACL twice in college football and lived with chronic knee pain for over a decade. BPC-157 gave me my legs back. I'm running 5Ks again. Not fast, but I'm running. That alone was worth every penny.",
    name: "Marcus Rivera",
    role: "Personal Trainer & Former College Athlete, Age 34",
    image: "https://images.unsplash.com/photo-1606335543586-137481155deb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxhdGhsZXRpYyUyMG1hbiUyMDMwcyUyMGZpdCUyMGNvbmZpZGVudCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NjE4MzY4M3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "As a physician, I was cautious about trying peptide therapy myself. But the clinical data behind these compounds is solid. After starting my own protocol, the improvement in my focus during 12-hour shifts has been remarkable. I now recommend it to select patients.",
    name: "Dr. Raj Patel, MD",
    role: "Internal Medicine Physician, Age 51",
    image: "https://images.unsplash.com/photo-1666887360369-1901f341fdad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxtYWxlJTIwZG9jdG9yJTIwd2hpdGUlMjBjb2F0JTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc2MTgzNjgzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "I tried everything for my skin and hormones after turning 40. Creams, supplements, diets. Nothing stuck. My nurse at PeptidesRecovery put me on a custom protocol and within 6 weeks, people started telling me I looked younger. My labs actually improved too.",
    name: "Karen Whitfield",
    role: "Real Estate Agent, Age 43",
    image: "https://images.unsplash.com/photo-1619369056679-87b6fbfac1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMDQwcyUyMGhlYWRzaG90JTIwbmF0dXJhbCUyMHNtaWxlfGVufDB8fHx8MTc3NjE4MzY4M3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "Running a company means stress, bad sleep, and skipping the gym for months. My doctor suggested peptide therapy after my cortisol levels came back through the roof. Three months in, I sleep 7 hours solid, my bloodwork is clean, and I feel sharper in meetings than I did at 35.",
    name: "James Caldwell",
    role: "CEO & Founder, Age 53",
    image: "https://images.unsplash.com/photo-1767175620484-1ed37931a0d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzc21hbiUyMGV4ZWN1dGl2ZSUyMDUwcyUyMGNvbmZpZGVudCUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjE4MzY4M3ww&ixlib=rb-4.1.0&q=85",
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[current];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
            Success Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#2A3324]">
            Real Results, Real People
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center min-h-[400px]">
          {/* Quote */}
          <div className="md:col-span-8">
            <Quote
              size={48}
              strokeWidth={1}
              className="text-[#C47C5D]/30 mb-6"
            />
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.5 }}
              >
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-serif font-light leading-snug text-[#2A3324] mb-8">
                  "{t.quote}"
                </blockquote>
                <div>
                  <p className="text-lg font-sans font-medium text-[#2A3324]">
                    {t.name}
                  </p>
                  <p className="text-sm font-sans font-light text-[#2A3324]/50">
                    {t.role}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots */}
            <div className="flex gap-3 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setCurrent(i)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === current
                      ? "bg-[#5C715E] w-8"
                      : "bg-[#2A3324]/15 hover:bg-[#2A3324]/30"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Portrait */}
          <div className="md:col-span-4 flex justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="w-64 h-64 md:w-72 md:h-72 rounded-full overflow-hidden border-4 border-[#EFECE5]"
              >
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
